import { Card } from '@/common/ui/Card/Card'
import { cn } from '@/common/lib/cn/cn'
import type { BrandComparisonBrandData } from '../../model/useCompanyStatsBrandComparison'
import { BRAND_COMPARE_COLORS, computeDelta } from '../../lib/brandComparison'
import { DASHBOARD_BLOCK_TYPES } from '../../lib/scoreColors'
import { getBlockTitle } from '../../lib/labels'
import { BrandComparisonDeltaBadge } from './BrandComparisonDeltaBadge'
import { CompanyStatsBrandComparisonLegendDot } from './CompanyStatsBrandComparisonHeader'

type CompanyStatsBrandComparisonContentBlocksProps = {
    brandA: BrandComparisonBrandData
    brandB: BrandComparisonBrandData
}

const LegendCapsule = ({ side }: { side: 'brandA' | 'brandB' }) => (
    <span
        className='inline-block h-1 w-4 shrink-0 rounded-full'
        style={{ backgroundColor: BRAND_COMPARE_COLORS[side] }}
        aria-hidden
    />
)

const BrandProgressRow = ({
    side,
    brandName,
    percent,
}: {
    side: 'brandA' | 'brandB'
    brandName: string
    percent: number
}) => {
    const color = BRAND_COMPARE_COLORS[side]
    const roundedPercent = Math.round(percent)

    return (
        <div className='flex flex-col gap-1.5'>
            <div className='flex items-center justify-between gap-3'>
                <span className='inline-flex min-w-0 items-center gap-2 text-sm text-base-content/80'>
                    <CompanyStatsBrandComparisonLegendDot side={side} />
                    <span className='truncate'>{brandName}</span>
                </span>
                <span className='text-sm font-medium tabular-nums' style={{ color }}>
                    {roundedPercent}%
                </span>
            </div>
            <div
                className='h-1.5 w-full overflow-hidden rounded-full bg-white/10'
                role='progressbar'
                aria-valuenow={roundedPercent}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`${brandName}: ${roundedPercent}%`}
            >
                <div
                    className='h-full rounded-full transition-all'
                    style={{ width: `${Math.min(100, Math.max(0, percent))}%`, backgroundColor: color }}
                />
            </div>
        </div>
    )
}

export const CompanyStatsBrandComparisonContentBlocks = ({
    brandA,
    brandB,
}: CompanyStatsBrandComparisonContentBlocksProps) => {
    const blocksA = brandA.scores.blocks.filter(b => DASHBOARD_BLOCK_TYPES.includes(b.block_type))
    const blocksBMap = new Map(brandB.scores.blocks.map(b => [b.block_type, b]))

    return (
        <Card color='primary' className='h-full'>
            <h3 className='text-xs font-bold uppercase tracking-wide text-white'>Блоки контента</h3>

            <div className='mt-4 flex flex-wrap items-center gap-4 text-sm text-base-content/70'>
                <span className='inline-flex items-center gap-2'>
                    <LegendCapsule side='brandA' />
                    {brandA.name}
                </span>
                <span className='inline-flex items-center gap-2'>
                    <LegendCapsule side='brandB' />
                    {brandB.name}
                </span>
            </div>

            <div className='mt-3 flex flex-col gap-5'>
                {blocksA.map(blockA => {
                    const blockB = blocksBMap.get(blockA.block_type)
                    const percentA = blockA.percent ?? 0
                    const percentB = blockB?.percent ?? 0
                    const label = getBlockTitle(blockA.block_type, blockA.title)
                    const delta = computeDelta(percentA, percentB)

                    return (
                        <div key={blockA.block_type} className={cn('flex flex-col gap-1')}>
                            <div className='flex items-center justify-between gap-1'>
                                <span className='text-sm font-medium text-base-content/90'>{label}</span>
                                <BrandComparisonDeltaBadge
                                    delta={delta}
                                    variant='block'
                                    brandAName={brandA.name}
                                    brandBName={brandB.name}
                                />
                            </div>

                            <div className='flex flex-col gap-3'>
                                <BrandProgressRow side='brandA' brandName={brandA.name} percent={percentA} />
                                <BrandProgressRow side='brandB' brandName={brandB.name} percent={percentB} />
                            </div>
                        </div>
                    )
                })}
            </div>
        </Card>
    )
}
